import {
  cloneElement,
  FC,
  isValidElement,
  ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { OverlayContext } from '../Overlay/OverlayContext';
import { registerOverlay } from '../Overlay/overlayStack';
import { positionAtAnchor } from '../Overlay/positioning';

import styles from './tooltip.module.scss';
import { TooltipCard } from './TooltipCard';

let nextTooltipId = 0;

interface ITooltipProps {
  content: ReactNode;
  children: ReactNode;
  interactiveChild?: boolean;
  hideBubble?: boolean;
  /** Compatibility: all tooltips now portal to avoid clipping. */
  // eslint-disable-next-line react/no-unused-prop-types
  escapeStackingContext?: boolean;
  showBubbleOnMount?: boolean;
}

const Tooltip: FC<ITooltipProps> = ({
  content,
  children,
  interactiveChild,
  hideBubble,
  showBubbleOnMount,
}) => {
  const intl = useIntl();
  const parentId = useContext(OverlayContext);
  const [layerId] = useState<object>(() => ({}));
  const [id] = useState(() => {
    nextTooltipId += 1;
    return `tooltip-${nextTooltipId}`;
  });
  const [isOpen, setIsOpen] = useState(Boolean(showBubbleOnMount));
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const visible = isOpen && !hideBubble;

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!visible || !trigger || !bubble) return undefined;
    const updatePosition = (): void =>
      setPosition(
        positionAtAnchor(
          trigger.getBoundingClientRect(),
          bubble.offsetWidth,
          bubble.offsetHeight,
          'top',
        ),
      );
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const observer = new ResizeObserver(updatePosition);
    observer.observe(trigger);
    observer.observe(bubble);
    const release = registerOverlay({
      id: layerId,
      parentId,
      element: bubble,
      root: bubble,
      restoreFocus: false,
      onDismiss: () => setIsOpen(false),
    });
    return () => {
      release();
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [visible, layerId, parentId]);

  const describedBy = visible ? id : undefined;
  const hasDisabledChild =
    interactiveChild &&
    isValidElement<{ disabled?: boolean }>(children) &&
    children.props.disabled;
  const trigger =
    interactiveChild && isValidElement<{ 'aria-describedby'?: string }>(children) ? (
      cloneElement(children, {
        'aria-describedby':
          [children.props['aria-describedby'], describedBy].filter(Boolean).join(' ') ||
          undefined,
      })
    ) : (
      <button
        type="button"
        className={styles.button}
        aria-describedby={describedBy}
        aria-label={
          typeof content === 'string'
            ? content
            : intl.formatMessage({ id: 'common.more-information' })
        }
      >
        {children}
      </button>
    );

  // A disabled control cannot receive focus; its wrapper exposes the explanation.
  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  return (
    <span
      ref={triggerRef}
      className={styles.trigger}
      tabIndex={hasDisabledChild ? 0 : undefined}
      role={hasDisabledChild ? 'group' : undefined}
      aria-describedby={hasDisabledChild ? describedBy : undefined}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
    >
      {trigger}
      {visible &&
        createPortal(
          <TooltipCard
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className={styles.bubble}
            style={position}
          >
            {content}
          </TooltipCard>,
          document.body,
        )}
    </span>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
};

export { Tooltip };
